/**
 * SelectField: the house drop-down. A native `select` with the browser's
 * own arrow removed (`appearance-none`) and a drawn chevron inside the
 * box, in the right padding the select reserves for it. The chosen text
 * ends before the chevron, and the chevron ends before the border, at
 * every width and at every text size, because both are laid out by the
 * page and not by the browser.
 *
 * Three sizes, all in rem so they follow the reader's text size:
 *   - `field` (2.875rem): the dialog form field, beside the modal form's
 *     inputs. It fills its column;
 *   - `default` (2.75rem): the house input and tab height;
 *   - `compact` (2.34375rem): bands, drawers, filters and table rows.
 * `default` and `compact` match SearchField's two sizes, so a select and
 * a search box on one band line up. `quiet` is borderless until hover,
 * for a select inside a table cell or a lane header.
 *
 * The select stays native: no custom list box, no portal, no key
 * handling. Its whole surface passes through (`id`, `name`, `value`,
 * `defaultValue`, the native `onChange` event, `onBlur`, `onFocus`,
 * `onKeyDown`, `required`, `disabled`, `autoComplete`, `aria-*`,
 * `data-testid`, `ref`), so role `combobox`, `selectOption` and
 * `fireEvent.change` all hold. `multiple` and the native `size` are not
 * offered: a list box is a different control.
 *
 * The accessible name comes from the caller: `<label htmlFor>` with
 * `id`, or `aria-label`. The wrapper is a `span`, never a `label`. The
 * chevron is decorative: `aria-hidden`, unfocusable, never a button,
 * and `pointer-events-none` so a click on it reaches the select. It
 * takes `currentColor`, so forced-colors modes recolour it with the
 * text, and it dims with a disabled select.
 *
 * `className` is for the wrapper only: the select's width and placement
 * in the caller's layout (`w-full`, `flex-1`, `ml-1`, `max-w-full`).
 * Text, padding, height, radius, border and background belong here, and
 * tools/lint-raw-selects.sh refuses them at a call site.
 */

import type { ComponentPropsWithRef, ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { FIELD_BOX } from './fieldBox';

type NativeSelectProps = Omit<ComponentPropsWithRef<'select'>, 'className' | 'size' | 'multiple'>;

export interface SelectFieldProps extends NativeSelectProps {
  /** field: the dialog form field, beside the modal form's inputs. default: 2.75rem, the house input height.
   *  compact: 2.34375rem, bands, drawers, filters and table rows. */
  size?: 'field' | 'default' | 'compact';
  /** Borderless until hover: a select inside a table cell or a lane header. */
  quiet?: boolean;
  /** Classes for the wrapper: its width and placement in the caller's layout. Never text, padding, height, border or background. */
  className?: string;
  children: ReactNode;
}

const SELECT = {
  base:
    'peer w-full min-w-0 appearance-none truncate bg-paper text-ink cursor-pointer ' +
    'transition-colors duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none ' +
    'disabled:cursor-not-allowed disabled:opacity-50',
  field: `${FIELD_BOX.field} pl-[0.875rem] pr-10`,
  default: `${FIELD_BOX.default} pl-3 pr-9`,
  compact: `${FIELD_BOX.compact} pl-2.5 pr-8`,
  quiet: 'border-transparent bg-transparent hover:border-sage-300 hover:bg-paper',
} as const;

const CHEVRON = {
  base: 'pointer-events-none absolute top-1/2 -translate-y-1/2 text-sage-500 peer-disabled:opacity-50',
  field: 'right-[0.875rem] h-4 w-4',
  default: 'right-3 h-4 w-4',
  compact: 'right-2.5 h-3.5 w-3.5',
} as const;

export function SelectField({
  size = 'default',
  quiet = false,
  className,
  children,
  ref,
  ...rest
}: SelectFieldProps): ReactNode {
  const placement = size === 'field' ? 'block w-full' : 'inline-block max-w-full align-middle';
  return (
    <span data-select-field="" className={`relative ${placement} min-w-0${className ? ` ${className}` : ''}`}>
      <select {...rest} ref={ref} className={`${SELECT.base} ${SELECT[size]}${quiet ? ` ${SELECT.quiet}` : ''}`}>
        {children}
      </select>
      <ChevronDown
        aria-hidden="true"
        focusable="false"
        data-select-chevron=""
        className={`${CHEVRON.base} ${CHEVRON[size]}`}
      />
    </span>
  );
}
